import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Stack, Skeleton, Box, Card, Group, Button } from '@repletoryreach/sdk-ui';
import { Alert, Tabs } from '@repletoryreach/sdk-ui/data-display';
import { useTenants } from '../../../data/useTenants';
import { TenantHeader } from './components/TenantHeader';
import { OverviewTab } from './components/OverviewTab';
import { UsersTab } from './components/UsersTab';
import { BillingTab } from './components/BillingTab';
import { IntegrationsTab } from './components/IntegrationsTab';
import { AuditTab } from './components/AuditTab';
import { SettingsTab } from './components/SettingsTab';
import { useT } from '../../../i18n/I18nProvider';

const TABS = ['overview', 'users', 'billing', 'integrations', 'audit', 'settings'] as const;

export default function TenantViewPage() {
  const t = useT();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { data, isLoading, error, refetch, isFetching } = useTenants();
  const tenant = data?.data.find((row) => row.id === id);

  // The open tab lives in the URL so a tenant's audit trail can be linked to,
  // Back steps between tabs and a refresh lands where the operator was.
  const requested = searchParams.get('tab');
  const activeTab = (TABS as readonly string[]).includes(requested ?? '') ? (requested as string) : 'overview';
  function selectTab(next: string) {
    const params = new URLSearchParams(searchParams);
    if (next === 'overview') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: true });
  }

  if (isLoading) {
    return (
      <Stack gap="lg">
        <Skeleton height={20} width="40%" />
        <Card withBorder><Stack gap="md"><Skeleton height={28} width="45%" /><Skeleton height={16} width="60%" /><Skeleton height={16} width="35%" /></Stack></Card>
        <Skeleton height={40} />
        <Card withBorder><Stack gap="md"><Skeleton height={18} /><Skeleton height={18} /><Skeleton height={18} /></Stack></Card>
      </Stack>
    );
  }

  // A failed fetch and a tenant that genuinely is not on the platform are
  // different answers, and only one of them is worth retrying.
  if (error) {
    return (
      <Stack gap="lg">
        <Alert color="error">
          <Stack gap="sm">
            <span>{t('tenants.loadError')}</span>
            <Group gap="sm">
              <Button size="xs" variant="outline" loading={isFetching} onClick={() => void refetch()}>{t('action.retry')}</Button>
            </Group>
          </Stack>
        </Alert>
      </Stack>
    );
  }

  if (!tenant) {
    return (
      <Stack gap="lg">
        <Alert color="warning">
          <Stack gap="sm">
            <span>{t('tenants.view.notFound')}</span>
            <Group gap="sm">
              <Button size="xs" variant="outline" onClick={() => navigate('/tenants')}>{t('tenants.view.header.backToAll')}</Button>
            </Group>
          </Stack>
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      {/* The shell header already carries Admin › Tenants › {name}, so the
          page does not draw a second breadcrumb beneath it. */}
      <TenantHeader tenant={tenant} />
      <Tabs value={activeTab} onChange={selectTab}>
        <Tabs.List>
          <Tabs.Tab value="overview">{t('common.overview')}</Tabs.Tab>
          <Tabs.Tab value="users">{t('tenants.view.tabs.users')}</Tabs.Tab>
          <Tabs.Tab value="billing">{t('tenants.view.tabs.billing')}</Tabs.Tab>
          <Tabs.Tab value="integrations">{t('tenants.view.tabs.integrations')}</Tabs.Tab>
          <Tabs.Tab value="audit">{t('tenants.view.tabs.audit')}</Tabs.Tab>
          <Tabs.Tab value="settings">{t('common.settings')}</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="overview">
          <Box py="lg"><OverviewTab tenant={tenant} /></Box>
        </Tabs.Panel>
        <Tabs.Panel value="users">
          <Box py="lg"><UsersTab tenantId={tenant.id} /></Box>
        </Tabs.Panel>
        <Tabs.Panel value="billing">
          <Box py="lg"><BillingTab tenantId={tenant.id} tenantName={tenant.name} /></Box>
        </Tabs.Panel>
        <Tabs.Panel value="integrations">
          <Box py="lg"><IntegrationsTab tenantId={tenant.id} /></Box>
        </Tabs.Panel>
        <Tabs.Panel value="audit">
          <Box py="lg"><AuditTab tenantId={tenant.id} /></Box>
        </Tabs.Panel>
        <Tabs.Panel value="settings">
          <Box py="lg"><SettingsTab tenant={tenant} /></Box>
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
